import type { HttpClient } from '~/shared/api'
import { ApiService, toBodyModel, toModel } from '~/shared/api'
import type { Logger } from '~/shared/interfaces'
import type { QueryParams } from '~/shared/interfaces/params'
import {
  ResearchDemandApplicationModel,
  ResearchDemandDetailModel,
  ResearchDemandFiltersModel,
  ResearchDemandQueryResultModel,
  ResearchDemandRequestModel,
} from '~/models/research-demand'
import type {
  ResearchDemandApplicationDto,
  ResearchDemandRequestDto,
} from '~/models/research-demand'
import { API_PREFIX } from '~/shared/const/request-services'
import { messages } from '~/shared/const/messages'

const RESEARCH_DEMAND_PAGE_SIZE = 4

export class ResearchDemandService extends ApiService {
  constructor(
    http: HttpClient,
    private readonly logger: Logger
  ) {
    super(http)
  }

  readonly urls = {
    filters: `${API_PREFIX}/nauchnye-razrabotki/filters`,
    query: `${API_PREFIX}/nauchnye-razrabotki/query`,
    bySlug: (slug: string) => `${API_PREFIX}/nauchnye-razrabotki/${slug}`,
    view: (slug: string) => `${API_PREFIX}/nauchnye-razrabotki/${slug}/add-view`,
    like: (slug: string) => `${API_PREFIX}/nauchnye-razrabotki/${slug}/add-like`,
    application: `${API_PREFIX}/nauchnye-razrabotki/requests`,
    contactRequest: `${API_PREFIX}/nauchnye-razrabotki/replies`,
  }

  getFilters(): Promise<ResearchDemandFiltersModel> {
    return this.http
      .request({ url: this.urls.filters, silent: true })
      .then(toBodyModel(ResearchDemandFiltersModel))
  }

  getQuery(params: QueryParams = {}, page: number = 1): Promise<ResearchDemandQueryResultModel> {
    return this.http
      .request({
        url: this.urls.query,
        params: { sort: '-created_at', ...params, 'page[size]': RESEARCH_DEMAND_PAGE_SIZE * page },
        silent: true,
      })
      .then(toBodyModel(ResearchDemandQueryResultModel))
  }

  getBySlug(slug: string): Promise<ResearchDemandDetailModel> {
    return this.http
      .request({ url: this.urls.bySlug(slug), silent: true })
      .then(toModel(ResearchDemandDetailModel))
  }

  async addView(slug: string): Promise<boolean> {
    try {
      await this.http.request({ method: 'POST', url: this.urls.view(slug), silent: true })
      return true
    } catch (error) {
      this.logger.warn(`Failed to add view for "${slug}"`, error)
      return false
    }
  }

  async addLike(slug: string): Promise<boolean> {
    try {
      await this.http.request({ method: 'POST', url: this.urls.like(slug), silent: true })
      return true
    } catch (error) {
      this.logger.warn(`Failed to like "${slug}"`, error)
      return false
    }
  }

  sendApplication(dto: ResearchDemandApplicationDto): Promise<ResearchDemandApplicationModel> {
    return this.http
      .request({
        method: 'POST',
        url: this.urls.application,
        data: dto,
        silent: true,
      })
      .then(toModel(ResearchDemandApplicationModel, messages.errors.create))
  }

  sendRequest(dto: ResearchDemandRequestDto): Promise<ResearchDemandRequestModel> {
    return this.http
      .request({
        method: 'POST',
        url: this.urls.contactRequest,
        data: dto,
        silent: true,
      })
      .then(toModel(ResearchDemandRequestModel, messages.errors.create))
  }
}
